/**
 * Urgency callout — low stock UI for cart (server-rendered via Section Rendering API)
 * and optional PDP client updates when inventory is loaded from Product JSON.
 *
 * Cart: no innerHTML updates required here; Shopify re-renders `main-cart-items` on change.
 * This module dispatches analytics/A-B friendly events and exposes PDP helpers.
 */
(function () {
  'use strict';

  var CONFIG_ID = 'urgency-callout-config';
  var CART_ROOT = '#main-cart-items';

  /**
   * @returns {{ threshold: number, masterEnabled: boolean, cartEnabled: boolean, pdpEnabled: boolean }}
   */
  function readConfig() {
    var el = document.getElementById(CONFIG_ID);
    if (!el) {
      return {
        threshold: 5,
        masterEnabled: true,
        cartEnabled: true,
        pdpEnabled: false,
        labelWithCountToken: 'Only __COUNT__ left!',
      };
    }
    try {
      var cfg = JSON.parse(el.textContent.trim());
      if (!cfg.labelWithCountToken) {
        cfg.labelWithCountToken = 'Only __COUNT__ left!';
      }
      return cfg;
    } catch {
      return {
        threshold: 5,
        masterEnabled: true,
        cartEnabled: true,
        pdpEnabled: false,
        labelWithCountToken: 'Only __COUNT__ left!',
      };
    }
  }

  /** @param {number} qty */
  function formatLabel(qty) {
    var cfg = readConfig();
    var t = cfg.labelWithCountToken || 'Only __COUNT__ left!';
    return String(t).replace('__COUNT__', String(qty));
  }

  /**
   * @param {Record<string, unknown>} variant - Product JSON variant
   * @param {number} threshold
   * @returns {boolean}
   */
  function shouldShowVariant(variant, threshold) {
    if (!variant || variant.inventory_management !== 'shopify') return false;
    var q = Number(variant.inventory_quantity);
    if (Number.isNaN(q) || q < 1 || q > threshold) return false;
    return true;
  }

  /**
   * Update a single callout root (PDP or dynamic fragment).
   * @param {HTMLElement} root - element with [data-urgency-callout]
   * @param {Record<string, unknown>} variant - from /products/:handle.js
   * @param {{ threshold?: number; labelTemplate?: (n: number) => string }} [opts]
   */
  function updateCalloutElement(root, variant, opts) {
    if (!root) return;
    var cfg = readConfig();
    if (!cfg.masterEnabled) {
      root.setAttribute('hidden', '');
      return;
    }
    var threshold = (opts && opts.threshold) ?? cfg.threshold;

    if (!shouldShowVariant(variant, threshold)) {
      root.setAttribute('hidden', '');
      return;
    }

    root.removeAttribute('hidden');
    root.setAttribute('data-inventory-quantity', String(variant.inventory_quantity));
    root.setAttribute('data-variant-id', String(variant.id));

    var qty = Number(variant.inventory_quantity);
    var textEl = root.querySelector('[data-urgency-callout-text]');
    if (textEl) {
      if (opts && typeof opts.labelTemplate === 'function') {
        textEl.textContent = opts.labelTemplate(qty);
      } else {
        textEl.textContent = formatLabel(qty);
      }
    }
  }

  function dispatchCartRefresh() {
    document.dispatchEvent(
      new CustomEvent('urgency-callout:cart-dom-updated', {
        bubbles: true,
        detail: { source: 'mutation' },
      }),
    );
  }

  function observeCartRoot() {
    var root = document.querySelector(CART_ROOT);
    if (!root || !window.MutationObserver) return;

    var mo = new MutationObserver(function () {
      dispatchCartRefresh();
    });
    mo.observe(root, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeCartRoot);
  } else {
    observeCartRoot();
  }

  window.UrgencyCallout = {
    readConfig: readConfig,
    shouldShowVariant: shouldShowVariant,
    updateCalloutElement: updateCalloutElement,
    /**
     * PDP: pass the callout container and variant from Product JSON after variant change.
     */
    refreshFromVariant: function (root, variant) {
      updateCalloutElement(root, variant, {});
    },
    /**
     * Future PDP wiring: fetches product JSON (cache-friendly) and updates element.
     * @param {HTMLElement} root
     * @param {string} handle
     * @param {number|string} variantId
     * @returns {Promise<void>}
     */
    hydrateFromProduct: function (root, handle, variantId) {
      var cfg = readConfig();
      if (!cfg.pdpEnabled) return Promise.resolve();

      var path = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
      if (!path.endsWith('/')) path += '/';
      return fetch(path + 'products/' + encodeURIComponent(handle) + '.js', {
        headers: { Accept: 'application/json' },
      })
        .then(function (r) {
          return r.json();
        })
        .then(function (product) {
          var v = (product.variants || []).find(function (x) {
            return String(x.id) === String(variantId);
          });
          if (v) updateCalloutElement(root, v, {});
        })
        .catch(function () {
          /* avoid breaking PDP if request fails */
        });
    },
  };
})();
